import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Laptop, Plus, Trash2, Lock, LockOpen, Loader2 } from "lucide-react";

const DEVICE_TYPES = ["Laptop", "Desktop", "Phone", "Tablet"];

export default function PortalDevices() {
  const { toast } = useToast();
  const [devices, setDevices] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", device_type: "Laptop", os: "", encrypted: false });

  const load = () => api.get("/employee-portal/devices").then((r) => setDevices(r.data.devices));
  useEffect(() => { load(); }, []);

  const addDevice = async () => {
    if (!form.name || !form.os) return;
    setSaving(true);
    try {
      await api.post("/employee-portal/devices", form);
      toast({ title: "Device added" });
      setOpen(false);
      setForm({ name: "", device_type: "Laptop", os: "", encrypted: false });
      load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/employee-portal/devices/${id}`);
    toast({ title: "Device removed" });
    load();
  };

  if (!devices) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portal-devices">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">My Devices</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" data-testid="portal-add-device-btn"><Plus className="w-3.5 h-3.5" /> Add device</Button>
          </DialogTrigger>
          <DialogContent data-testid="portal-add-device-dialog">
            <DialogHeader><DialogTitle>Register a device</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Device name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dan's MacBook Pro" data-testid="portal-device-name-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v })}>
                  <SelectTrigger data-testid="portal-device-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEVICE_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Operating system</Label>
                <Input value={form.os} onChange={(e) => setForm({ ...form, os: e.target.value })} placeholder="e.g. macOS 15.1" data-testid="portal-device-os-input" />
              </div>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={form.encrypted} onChange={(e) => setForm({ ...form, encrypted: e.target.checked })} data-testid="portal-device-encrypted-checkbox" />
                Disk encryption enabled
              </label>
            </div>
            <DialogFooter>
              <Button onClick={addDevice} disabled={saving} data-testid="portal-device-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add device"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {devices.map((d) => (
          <Card key={d.id} data-testid={`portal-device-row-${d.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Laptop className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium truncate">{d.name}</div>
                  <div className="text-[12px] text-muted-foreground">{d.device_type} · {d.os}</div>
                  <Badge variant="outline" className={`mt-1 gap-1 text-[10px] ${d.encrypted ? "text-emerald-600 border-emerald-500/40" : "text-amber-600 border-amber-500/40"}`}>
                    {d.encrypted ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />} {d.encrypted ? "Encrypted" : "Not encrypted"}
                  </Badge>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => remove(d.id)} data-testid={`portal-device-remove-btn-${d.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </CardContent>
          </Card>
        ))}
        {devices.length === 0 && <p className="text-[13px] text-muted-foreground">No devices registered yet.</p>}
      </div>
    </div>
  );
}
